import { useTranslation } from 'react-i18next';
import { Button, Modal, ModalActions, ModalContent, ModalTitle } from '@c2fo/liquidity';
import { ArrowUpRightFromSquareIcon } from '@c2fo/liquidity/icons';
import { useReporting } from '@/reporting';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { createFiqUrl } from './createFiqUrl';
import type { BuyerCashflowData } from './useBuyerCashflow';
import type { FinanceIQCustomerInfo } from './useFinanceIQCustomerNames';

export type FinanceIQBreakdownKpiType = 'incoming_cash' | 'deductions' | 'past_due' | 'chart_period';

type Customer = BuyerCashflowData['byCustomer'][number];

interface KpiConfig {
  filter: (c: Customer) => boolean;
  sortAmount: (c: Customer) => number;
  displayAmount: (c: Customer) => number;
  displayCount: (c: Customer) => number;
  labelKey: 'core.invoiceWithCount' | 'core.deductionWithCount';
  /** When true, navigates to the deductions tab in FinanceIQ. */
  isDeductions: boolean;
  statuses: string[];
  /** When true, renders separate invoice and deduction buttons per customer row. */
  showBothLabels?: boolean;
}

const KPI_CONFIGS: Record<FinanceIQBreakdownKpiType, KpiConfig> = {
  chart_period: {
    filter: (c) =>
      c.totalInvoiceAmount !== 0 ||
      c.totalInvoiceCount !== 0 ||
      c.totalDeductionAmount !== 0 ||
      c.totalDeductionCount !== 0,
    sortAmount: (c) => c.totalInvoiceAmount,
    displayAmount: (c) => c.totalInvoiceAmount,
    displayCount: (c) => c.totalInvoiceCount,
    labelKey: 'core.invoiceWithCount',
    isDeductions: false,
    statuses: [],
    showBothLabels: true,
  },
  incoming_cash: {
    filter: (c) => c.totalInvoiceAmount !== 0 || c.totalInvoiceCount !== 0,
    // Show the net-of-early-pay-discount figure so the per-buyer rows reconcile with the
    // incoming-cash top line (which uses incomingCashAmount). See FIQ-416.
    sortAmount: (c) => c.incomingCashAmount,
    displayAmount: (c) => c.incomingCashAmount,
    displayCount: (c) => c.incomingCashCount,
    labelKey: 'core.invoiceWithCount',
    isDeductions: false,
    statuses: [],
  },
  deductions: {
    filter: (c) => c.totalDeductionAmount !== 0 || c.totalDeductionCount !== 0,
    sortAmount: (c) => Math.abs(c.totalDeductionAmount),
    displayAmount: (c) => -Math.abs(c.totalDeductionAmount),
    displayCount: (c) => c.totalDeductionCount,
    labelKey: 'core.deductionWithCount',
    isDeductions: true,
    statuses: [],
  },
  past_due: {
    filter: (c) => c.pastDueAmount !== 0 || c.pastDueCount !== 0,
    sortAmount: (c) => c.pastDueAmount,
    displayAmount: (c) => c.pastDueAmount,
    displayCount: (c) => c.pastDueCount,
    labelKey: 'core.invoiceWithCount',
    isDeductions: false,
    statuses: ['PAST_DUE'],
  },
};

const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/).slice(0, 2);
  return words.map((w) => w[0]?.toUpperCase() ?? '').join('');
};

const buildDetailUrl = ({
  makerOrganizationUuid,
  statuses,
  isDeductions,
  currency,
  effectivePayDateFrom,
  effectivePayDateTo,
  transactionDateFrom,
  transactionDateTo,
}: {
  makerOrganizationUuid: string;
  statuses: string[];
  isDeductions: boolean;
  currency: string;
  effectivePayDateFrom?: string;
  effectivePayDateTo?: string;
  transactionDateFrom?: string;
  transactionDateTo?: string;
}): string => {
  const tab = isDeductions ? 'deductions' : 'invoices';
  const routeParams: Record<string, string> = {};
  if (statuses.length > 0) routeParams.status = statuses.join(',');
  if (transactionDateFrom) routeParams.startDate = transactionDateFrom;
  if (transactionDateTo) routeParams.endDate = transactionDateTo;
  if (effectivePayDateFrom) routeParams.effectivePayDateFrom = effectivePayDateFrom;
  if (effectivePayDateTo) routeParams.effectivePayDateTo = effectivePayDateTo;
  // Display-only currency signal. Never send `currency` here — on /insights that is a filter
  // param and would restrict the detail view to a single currency (FIQ-410).
  if (currency) routeParams.preferredCurrency = currency;
  return createFiqUrl(`/insights/${makerOrganizationUuid}/${tab}`, routeParams);
};

export interface FinanceIQBreakdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  kpiType: FinanceIQBreakdownKpiType;
  title: string;
  effectivePayDateFrom?: string;
  effectivePayDateTo?: string;
  transactionDateFrom?: string;
  transactionDateTo?: string;
  data: {
    byCustomer: BuyerCashflowData['byCustomer'];
    currency: string;
  };
  customerNames: Record<string, FinanceIQCustomerInfo>;
}

export const FinanceIQBreakdownModal = ({
  isOpen,
  onClose,
  kpiType,
  title,
  effectivePayDateFrom,
  effectivePayDateTo,
  transactionDateFrom,
  transactionDateTo,
  data,
  customerNames,
}: FinanceIQBreakdownModalProps) => {
  const { t } = useTranslation();
  const { asNumber, asCurrencyCompact, asDateStringText } = useLocaleFormat();
  const { trackEnterpriseEvent } = useReporting();

  const kpiConfig = KPI_CONFIGS[kpiType];

  const sortedCustomers = [...data.byCustomer]
    .filter(kpiConfig.filter)
    .sort((a, b) => kpiConfig.sortAmount(b) - kpiConfig.sortAmount(a));

  const navigateToDetail = (c: Customer, forceIsDeductions?: boolean) => {
    const isDeductions = forceIsDeductions ?? kpiConfig.isDeductions;
    const url = buildDetailUrl({
      makerOrganizationUuid: c.makerOrganizationUuid,
      statuses: kpiConfig.statuses,
      isDeductions,
      currency: data.currency,
      effectivePayDateFrom,
      effectivePayDateTo,
      transactionDateFrom,
      transactionDateTo,
    });
    trackEnterpriseEvent('financeiq-widget::breakdown-modal::customer::clicked', {
      kpiType,
      tab: isDeductions ? 'deductions' : 'invoices',
      currency: data.currency,
    });
    window.open(url, '_blank', 'noopener,noreferrer');
    onClose();
  };

  const handleCustomerClick = (c: Customer) => {
    // For chart_period, default to invoices unless the customer has no invoices at all.
    const forceDeductions =
      kpiConfig.showBothLabels && c.totalInvoiceAmount === 0 && c.totalInvoiceCount === 0 ? true : undefined;
    navigateToDetail(c, forceDeductions);
  };

  const fmtDate = (d: string) => asDateStringText({ date: d, isUTC: true });
  const rangeFrom = effectivePayDateFrom ?? transactionDateFrom;
  const rangeTo = effectivePayDateTo ?? transactionDateTo;
  const formattedDateRange =
    rangeFrom && rangeTo
      ? rangeFrom === rangeTo
        ? fmtDate(rangeFrom)
        : `${fmtDate(rangeFrom)} – ${fmtDate(rangeTo)}`
      : '';

  return (
    <Modal open={isOpen} onClose={onClose} size="sm">
      <ModalTitle>{title}</ModalTitle>
      <ModalContent className="space-y-4">
        <p className="text-sm text-text-secondary">{formattedDateRange}</p>

        {sortedCustomers.length === 0 && (
          <p className="py-4 text-center text-sm text-text-secondary">{t('financeiq.breakdown.noData')}</p>
        )}

        {sortedCustomers.length > 0 && (
          <>
            <p className="text-sm text-text-secondary">{t('financeiq.breakdown.selectCustomerPrompt')}</p>
            <div className="space-y-3">
              {sortedCustomers.map((customer) => {
                const customerInfo = customerNames[customer.makerOrganizationUuid];
                const displayName = customerInfo?.displayName ?? customer.makerOrganizationUuid;
                const logoUrl = customerInfo?.logoUrl ?? null;

                const buildLabel = (amount: number, count: number, labelKey: KpiConfig['labelKey']) =>
                  `${asCurrencyCompact(amount, data.currency)} (${asNumber({ value: count, decimals: 0 })}) ${t(
                    labelKey,
                    { count, formattedCount: '' }
                  ).trim()}`;

                const hasInvoices = customer.totalInvoiceAmount !== 0 || customer.totalInvoiceCount !== 0;
                const hasDeductions = customer.totalDeductionAmount !== 0 || customer.totalDeductionCount !== 0;

                const invoiceLabel =
                  kpiConfig.showBothLabels && hasInvoices
                    ? buildLabel(customer.totalInvoiceAmount, customer.totalInvoiceCount, 'core.invoiceWithCount')
                    : null;
                const deductionLabel =
                  kpiConfig.showBothLabels && hasDeductions
                    ? buildLabel(
                        -Math.abs(customer.totalDeductionAmount),
                        customer.totalDeductionCount,
                        'core.deductionWithCount'
                      )
                    : null;
                const singleLabel = !kpiConfig.showBothLabels
                  ? buildLabel(kpiConfig.displayAmount(customer), kpiConfig.displayCount(customer), kpiConfig.labelKey)
                  : null;

                return (
                  <div
                    key={customer.makerOrganizationUuid}
                    role="button"
                    tabIndex={0}
                    aria-label={t('financeiq.breakdown.viewInFinanceIQ')}
                    className="group flex w-full items-center gap-4 rounded-lg border border-gray-200 bg-white p-4 transition-all hover:border-blue-300 hover:bg-blue-50 hover:shadow-sm active:scale-[0.98]"
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('[data-invoice-btn],[data-deduction-btn]')) return;
                      handleCustomerClick(customer);
                    }}
                    onKeyDown={(e) => {
                      if (!(e.target instanceof Element)) return;
                      if (e.target.closest('[data-invoice-btn],[data-deduction-btn]')) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCustomerClick(customer);
                      }
                    }}
                  >
                    {/* Avatar — logo when available, otherwise gray fallback + initials */}
                    <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100">
                      {logoUrl ? (
                        <img src={logoUrl} alt={displayName} className="h-9 w-auto max-w-9 object-contain" />
                      ) : (
                        <p className="whitespace-nowrap text-base font-medium text-gray-600">
                          {getInitials(displayName)}
                        </p>
                      )}
                    </div>

                    {/* Name + amount */}
                    <div className="flex w-full flex-col items-start gap-1 text-left">
                      <p className="text-base font-semibold text-gray-900 group-hover:text-blue-700">{displayName}</p>
                      <div className="flex flex-wrap gap-2 text-sm text-gray-600 group-hover:text-gray-800">
                        {singleLabel}
                        {invoiceLabel && (
                          <button
                            type="button"
                            data-invoice-btn
                            className="hover:text-blue-600"
                            onClick={() => navigateToDetail(customer, false)}
                          >
                            {invoiceLabel}
                          </button>
                        )}
                        {deductionLabel && (
                          <button
                            type="button"
                            data-deduction-btn
                            className="hover:text-blue-600"
                            onClick={() => navigateToDetail(customer, true)}
                          >
                            {deductionLabel}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* External link icon indicates cross-app navigation */}
                    <ArrowUpRightFromSquareIcon className="size-5 shrink-0 text-gray-400 transition-colors group-hover:text-blue-600" />
                  </div>
                );
              })}
            </div>
          </>
        )}
      </ModalContent>
      <ModalActions>
        <Button variant="secondary" onClick={onClose}>
          {t('core.close')}
        </Button>
      </ModalActions>
    </Modal>
  );
};
